import axios from 'axios';
import { isEmpty } from 'lodash';
import Constants from 'constants';
import APP from 'app';
import apiConfig from 'configs/services/apiConfig';
import configObj from 'config';
import errObj from 'viewObjects/Globals/errObj';
import { detect } from 'detect-browser';
import tokenService from 'services/tokenService';
import configHelper from 'helper/Config/configHelper';
import validateUser from './validateUser';
import utils from '../app/utils';
import guestLoginConnector from './connect/user/guestLoginConnector';

const { isRelativeUrl = false, b2cGuestToken = '', } = APP.Utils.getConfig();
const { serviceProtocol = '', serviceDomain = '', } = APP.Utils.fetchGlobal();

/**
 * If uuid is undefined in case for b2c, do guest Login
 */

const handleUndefinedToken = async (api) => {
    if (!(api === 'guestLogin') && !APP.Utils.getJwtToken()) {
        if (b2cGuestToken) {
            APP.Utils.saveToCookie(b2cGuestToken);
        } else {
            await guestLoginConnector();
        }
    }
}

/**
 * It will build the Req data for the external API according to GET and POST method
 * @params Options
 * @return JSON
 */
const _buildReqData = async (options) => {
    const { content = {}, api = '', headers = {}, urlAppend = '', extraHeaders = {} } = options,
        apiDetail = apiConfig[api],
        { server = 'ums_srv', url, method } = apiDetail,
        { protocol, server: apiServer, proxyUrl = '' } = configObj.serverConfig[server],
        env = process.env.NODE_ENV || 'development',
        baseUrl = isRelativeUrl && !proxyUrl ? '' : (APP.Utils.isWhitelabel() && env !== 'development') ?
            `${serviceProtocol}://${ serviceDomain || apiServer}` : `${protocol}://${apiServer || serviceDomain }`,
        apiUrl = `${proxyUrl}${baseUrl}${url}${urlAppend}`;
    if (APP.Utils.isB2cClient()) {
        await handleUndefinedToken(api);
    }
    let postData = setPostData(content, headers, extraHeaders);
    return {
        url: apiUrl,
        method,
        ...postData
    };
},

    /**
     * It will set the POST data for the request
     * @param content
     * @return JSON
     */
    setPostData = (content, headers, extraHeaders = {}) => {
        const { responseType = '' } = extraHeaders
        const browser = detect();
        const _headers = {
            "browserName": browser.name,
            "browserVersion": browser.version,
            ...setAuthorizationHeader(),
            ...headers
        };
        return {
            data: content,
            headers: extraHeaders ?
                {
                    ..._headers,
                    ...extraHeaders
                } : {
                    ..._headers
                },
            responseType: responseType
        };
    },

    /**
     * It will set the Authorization Header for the Request
     * @return JSON
     */
    setAuthorizationHeader = () => {
        const authorizationToken = APP.Utils.getJwtToken();
        const { currEnv = 'test' } = configObj;
        const { partnerId = '' } = APP.Utils.getUserInfoFromJwt();
        const whitelabel = APP.Utils.isWhitelabel() ? APP.Utils.getWhitelabelKey().toString() : ''
        return {
            'Content-Type': 'application/json',
            'Authorization': decodeURIComponent(authorizationToken),
            'crossDomain': true,
            'Access-Control-Allow-Origin': '*',
            'channelType': APP.Utils.isMobile() ? 'MOBILE' : 'DESKTOP',
            currEnv,
            whitelabel,
            partnerId
        };
    },

    /**
     * It will set the JWT Cookie if receive in the response header
     */
    setJwtCookie = (headers) => {
        const { authorization = '' } = headers;
        if (!isEmpty(authorization) && 'string' == typeof authorization) {
            APP.Utils.saveToCookie(authorization);
        }
    },

    /**
 * @description redirects to desired URL
 * @param url
 */
    redirectToApi = (url) => {
        window.location.href = url;
    },

    /*** This Function sets tokens in LS using config service*/
    setConfigTokens = async (headers = {}) => {
        const { ui_conf = '' } = headers;
        if (!isEmpty(ui_conf) && utils.isJsonString(ui_conf)) {
            const { data: configTokens = {} } = JSON.parse(ui_conf);
            if (!isEmpty(configTokens) && typeof configTokens == "object") {
                const previousTokens = await configHelper.decryptAndGet(Constants.module.CONFIG_TOKENS);
                if (isEmpty(previousTokens)) {
                    await configHelper.encryptJSONAndSave(Constants.module.CONFIG_TOKENS, configTokens);
                }
                const _configKey = APP.Utils.isWhitelabel() ? `${APP.Utils.getWhitelabelKey()}_${Constants.module.CACHE_TOKENS.HOTEL_CONFIG}` : Constants.module.CACHE_TOKENS.HOTEL_CONFIG;
                const _changedTokens = configHelper.isTokenChanged([
                    _configKey
                ], previousTokens, configTokens);
                if (!isEmpty(_changedTokens)) {
                    await configHelper.encryptJSONAndSave(Constants.module.CONFIG_TOKENS, configTokens);
                    tokenService.dispatchEvent(_changedTokens, configTokens);
                }
            }
        }
    },

    /**
     * This function will sanitize the response before sending it to client. 
     * Also it will handle the error scenarios
     */
    sanitizeResponse = (response = {}, options = {}, requestType) => {
        const { ignoreErrorMessage = false, ignoreAllError = false } = options;
        let { data = {}, headers = {} } = response;
        setJwtCookie(headers);
        setConfigTokens(headers);
        const { validUser, accessDeniedData = {} } = validateUser(headers);
        if (!validUser && !APP.Utils.isB2C()) {
            data = accessDeniedData;
        }
        try {
            const { status = '' } = data,
                { success = false } = status;
            if (success || status === 'OK') {
                const { redirectionurl = '' } = headers;
                if (redirectionurl) { redirectToApi(redirectionurl); return; }
                if ('object' === typeof data && !isEmpty(data)) {
                    APP.MessageBus.trigger(Constants.actions.ERROR_HANDLER.HIDE_ERROR_MESSAGE, {
                        success: data.status.success
                    });
                    return data;
                }
            } else {
                let _errors = errObj;
                if (!isEmpty(data.errors)) {
                    _errors = data.errors;
                    const [firstError = {}] = data.errors;
                    const { errCode = '' } = firstError;
                    if ((!Constants.common.IGNORE_ERROR_MESSAGE.includes(errCode) || !ignoreErrorMessage) && !ignoreAllError) {
                        APP.MessageBus.trigger(Constants.actions.ERROR_HANDLER.DISPLAY_ERROR_MESSAGE, {
                            success: data.status.success,
                            errors: _errors
                        });
                        setTimeout(() => {
                            if (!isEmpty(data.errors) && '403' == data.errors[0].errCode) {
                                APP.Utils.removeCookie('uuid');
                                window.location.href = `${configObj.flightProductUrl}/app/login`;
                            }
                        }, 2000);
                    }
                }
                return data;
            }
        } catch (e) {
            APP.MessageBus.trigger(Constants.actions.ERROR_HANDLER.DISPLAY_ERROR_MESSAGE,
                { errObj });
            return false;
        }
        return false;
    },

    externalApiRequest = async (options, requestType = '') => {
        APP.MessageBus.trigger(Constants.actions.ERROR_HANDLER.HIDE_ERROR_MESSAGE);
        const reqData = await _buildReqData(options);
        return axios(reqData).then((responseData) => {
            return sanitizeResponse(responseData, options, requestType);
        }).catch((err) => {
            if (err) {
                APP.Utils.log(err);
            }
            return sanitizeResponse(err.response, options, requestType);
        });
    };

export default externalApiRequest;
